// https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/installation/#using-npm

import { getEnv } from '@/composables/useEnv'
import { assignedExperiments } from '@/composables/useExperiment'
import { useHandleError } from '@/composables/useHandleError'
import { useUserStore } from '@/store/user'
import { IUserFromToken } from '@lifk/types'
import { RudderAnalytics } from '@rudderstack/analytics-js/bundled'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

export interface IEventDetails {
  object: 'button' | 'form' | 'link' | 'modal'
  event: 'click' | 'submit' | 'view'
  // Please refer to this naming convention for actions:
  // https://mixpanel.com/blog/selecting-events-and-properties-with-extensibility-and-targeted-reporting-in-mind/
  action: string // SNAKE CASE!!! Examples: 'logged_in', 'opened_messenger', 'created_sketch' --> !!! use past tense !!!
  location: string // SNAKE CASE!!! Semantic location name (ie `create_sketch_modal`)
  value?: string | object // SNAKE CASE!!! Additional state value selected, for example an I18nString or object with additional properties
}

let rudderAnalytics

const experimentProperties = Object.fromEntries(
  Object.entries(assignedExperiments).map(([k, v]) => [`exp_${k}`, v])
)

export function useAnalytics() {
  const route = useRoute()
  const userStore = useUserStore()
  const writeKey = getEnv('VITE_APP_RUDDERSTACK_CLIENT_WRITE_KEY')
  const rudderstackUrl = getEnv('VITE_APP_RUDDERSTACK_DATA_PLANE_URL')
  const appEnv = getEnv('VITE_APP_ENV')
  const { logError } = useHandleError()

  const userHasToolAnalytics = computed(() => {
    return userStore.user?.toolAnalytics
  })

  const runAnalytics = computed(() => {
    // disable analytics in iframes
    if (route.name?.toString().startsWith('embed-map')) return false

    // To test local analytics, uncomment the line below and make sure
    // to use the correct env variables for Rudderstack (FieldKitServer Dev Source)
    return (
      appEnv === 'production' || appEnv === 'staging' || appEnv === 'review' // || appEnv === 'development'
    )
  })

  // Treat rudderAnalytics as a singleton as ins tructed in docs
  if (
    !rudderAnalytics &&
    writeKey &&
    rudderstackUrl &&
    runAnalytics.value &&
    userHasToolAnalytics.value
  ) {
    rudderAnalytics = new RudderAnalytics()
    rudderAnalytics.load(writeKey, rudderstackUrl)
  }

  const trackPage = (name: string) => {
    if (!rudderAnalytics) return

    if (userStore.user && userStore.user.role !== 'guest') {
      identifyUser(userStore.user)
    }
    try {
      rudderAnalytics.page(name, {
        context: 'app'
      })
    } catch (e) {
      logError(e, 'Rudderstack page error')
    }
  }

  const identifyUser = (user: IUserFromToken) => {
    if (!rudderAnalytics) return

    let name = user.firstName ?? ''
    name += user.lastName ? ` ${user.lastName}` : ''

    try {
      rudderAnalytics.identify(user._id, {
        name,
        email: user.email,
        company: user.organization
      })
    } catch (e) {
      logError(e, 'Rudderstack identify error')
    }
  }

  const trackEvent = (details: IEventDetails) => {
    // Don't track if analytics is disabled or rudderAnalytics is not initialized
    if (!rudderAnalytics || !userHasToolAnalytics.value) return

    if (userStore.user && userStore.user.role !== 'guest') {
      identifyUser(userStore.user)
    }

    // Always include default parameters
    const defaultProperties = {
      origin: 'client',
      context: 'app',
      ...experimentProperties
    }

    const properties = {
      ...defaultProperties,
      ...details
    }

    try {
      // Construct event name based on details per naming convention
      // {entity}_{action}
      rudderAnalytics.track(`app_${details.action}`, properties)
    } catch (e) {
      logError(e, 'Rudderstack event error')
    }
  }

  const trackButtonClick = (details: {
    // Keep object to enforce strict convention schema
    action: string
    location: string
    value?: string | object
  }) => {
    const eventDetails: IEventDetails = {
      object: 'button',
      event: 'click',
      ...details
    }

    trackEvent(eventDetails)
  }

  const trackLinkClick = (details: {
    // Keep object to enforce strict convention schema
    action: string
    location: string
    value?: string | object
  }) => {
    const eventDetails: IEventDetails = {
      object: 'link',
      event: 'click',
      ...details
    }

    trackEvent(eventDetails)
  }

  const trackFormSubmit = (details: {
    // Keep object to enforce strict convention schema
    action: string
    location: string
    value?: string | object
  }) => {
    const eventDetails: IEventDetails = {
      object: 'form',
      event: 'submit',
      ...details
    }

    trackEvent(eventDetails)
  }

  return {
    trackPage,
    trackEvent,
    identifyUser,
    trackButtonClick,
    trackLinkClick,
    trackFormSubmit
  }
}
